import { Component, OnInit, Inject, OnDestroy, ElementRef, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { BannerInfo, LayoutService } from '../layout.service';
import { distinctUntilChanged, fromEvent, Subject, takeUntil } from 'rxjs';
import { LogHelper } from '../../logging/log-helper';
import { ResponsiveService, ViewState } from '../../responsive.service';

const bannerDefaultBg = '#ffa500';
const bannerDefaultFg = '#ffffff';

@Component({
	selector: 'app-layout-banner',
	templateUrl: './banner.component.html',
	styleUrls: ['./banner.component.scss'],
	standalone: false
})
export class BannerComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('bannerContainer') bannerContainer!: ElementRef;

	get config(): BannerInfo { return this.layoutService.bannerInfo$.value; }
	get shouldDisplayBanner(): boolean { return this.config?.enabled === true && this.config?.ignored !== true; }
	get bannerBackgroundColor(): string { return this.config?.background ?? bannerDefaultBg; }
	get bannerForegroundColor(): string { return this.config?.foreground ?? bannerDefaultFg; }
	get bannerFontSize(): string { return this.responsive.state.isSmallish ? '14px' : '16px'; }
	
	private readonly log = new LogHelper('Banner');
  private readonly destroy$ = new Subject<void>();

	constructor(
		private readonly layoutService: LayoutService,
		private readonly responsive: ResponsiveService,
		private readonly changeDetector: ChangeDetectorRef,
		@Inject(L10N_LOCALE) public locale: L10nLocale,
	) { }

	ngOnInit(): void {
		// listen for banner config updates
		this.layoutService.bannerInfo$.pipe(takeUntil(this.destroy$), distinctUntilChanged(
			(x: BannerInfo, y: BannerInfo) => JSON.stringify(x ?? {}) === JSON.stringify(y ?? {})
		)).subscribe(info => {
			this.log.debug(`config updated: enabled=${info?.enabled}`);
			this.changeDetector.markForCheck();
		});

		// listen for viewport breakpoint changes (small <-> large)
    this.responsive.updates.pipe(takeUntil(this.destroy$), distinctUntilChanged(
			(x: ViewState, y: ViewState) => x.isSmallish === y.isSmallish
		)).subscribe(state => {
			this.log.debug(`viewport updated: ${state.isSmallish ? 'small' : 'large'}`);
			this.changeDetector.markForCheck();
    });
	}
  
  ngAfterViewInit() {
    // Initial check
    this.checkHeight('initial check');
    
    // listen for DOM changes (banner enabled/disabled)
    const observer = new MutationObserver(() => this.checkHeight('DOM update'));
    observer.observe(this.bannerContainer.nativeElement, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true
    });
    this.destroy$.subscribe(() => observer.disconnect());
    
    // listen for window resize events which might affect layout
    fromEvent(window, 'resize')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkHeight('window resized');
      });
  }
  
  private lastHeight = 0;
  private checkHeight(source: string) {
		const nativeDiv = this.bannerContainer.nativeElement as HTMLElement;
    const currentHeight = nativeDiv?.scrollHeight ?? 0;
    
    if (this.lastHeight !== currentHeight) {
      this.log.debug(`banner height changed: source=${source}; height=${currentHeight}`);
      this.lastHeight = currentHeight;
			const info = this.layoutService.currentLayoutInfo;
			info.bannerHeight = currentHeight;
			this.layoutService.publishLayoutUpdate(info);
    }
  }

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
